// MUI theme for the Truck Stops page (TruckStops.jsx) ONLY. The page passes it
// to a nested ThemeProvider, so it extends the app theme for every control on
// the page, including the Add/Edit stop dialog and the select menus, which are
// portaled in the DOM but still sit inside the React tree.
//
// Palette rule for this page: no green, no blue or indigo. Primary is ink
// (#0F172A), the accent is amber, and the focus ring is burnt orange.
import { createTheme } from '@mui/material/styles'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import { JAKARTA, typographyWithFont } from '../utils/plusJakartaTheme'

export const TS = {
  font: JAKARTA,
  text: '#0F172A',
  label: '#1E293B',
  secondary: '#475467',
  icon: '#344054',
  page: '#F4F5F7',
  card: '#FFFFFF',
  border: '#E1E5EA',
  btnBorder: '#D0D5DD',
  primary: '#0F172A',
  primaryHover: '#1E293B',
  amber: '#F59E0B',
  amberSoft: '#FFF1D6',
  amberIcon: '#B45309',
  amberText: '#92400E',
  inputBorder: '#8793A5',
  placeholder: '#667085',
  chipBg: '#F1F3F6',
  tileBg: '#E7EAEF',
  bannerSub: '#C3CAD6',
  danger: '#B91C1C',
  dangerBg: '#FEF2F2',
  dangerBorder: '#F3B8B8',
  focus: '#C2410C'
}

export const focusRing = { outline: `2px solid ${TS.focus}`, outlineOffset: '2px' }

const components = {
  MuiTypography: {
    styleOverrides: {
      root: { color: TS.text },
      colorTextSecondary: { color: TS.secondary }
    }
  },
  MuiPaper: {
    styleOverrides: { root: { color: TS.text } }
  },
  MuiCircularProgress: {
    styleOverrides: { colorPrimary: { color: TS.primary } }
  },

  // ---- Fields: label above, 48px input ----
  // Every label sits above its field, so it is always "shrunk" (which also
  // keeps placeholders visible) and the outline never cuts a notch for it.
  MuiInputLabel: {
    defaultProps: { shrink: true },
    styleOverrides: {
      root: {
        position: 'relative',
        transform: 'none',
        maxWidth: 'none',
        overflow: 'visible',
        whiteSpace: 'normal',
        marginBottom: 6,
        fontSize: 13,
        fontWeight: 600,
        lineHeight: 1.4,
        color: TS.label,
        '&.Mui-focused': { color: TS.label },
        '&.Mui-error': { color: TS.danger }
      }
    }
  },
  MuiInputBase: {
    styleOverrides: { input: { color: TS.text } }
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: 10,
        backgroundColor: TS.card,
        fontSize: 15,
        color: TS.text,
        '& .MuiOutlinedInput-notchedOutline': { borderColor: TS.inputBorder },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: TS.secondary },
        '&.Mui-focused': focusRing,
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TS.text, borderWidth: 1 }
      },
      notchedOutline: {
        top: 0,
        '& legend': { display: 'none' }
      },
      input: {
        color: TS.text,
        height: 48,
        boxSizing: 'border-box',
        padding: '0 14px',
        '&::placeholder': { color: TS.placeholder, opacity: 1 }
      }
    }
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: { marginLeft: 0, marginRight: 0, marginTop: 6, fontSize: 13, lineHeight: 1.45, color: TS.secondary }
    }
  },
  MuiSelect: {
    defaultProps: { IconComponent: KeyboardArrowDownRoundedIcon },
    styleOverrides: {
      select: {
        color: TS.text,
        '&.MuiSelect-select': {
          minHeight: 48,
          height: 'auto',
          display: 'flex',
          alignItems: 'center',
          boxSizing: 'border-box'
        },
        '&&&': { paddingRight: 44 }
      },
      icon: { right: 12, color: TS.secondary, fontSize: 22 }
    }
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        color: TS.text,
        fontSize: 15,
        minHeight: 44,
        '&:hover': { backgroundColor: TS.page },
        '&.Mui-selected': { backgroundColor: TS.chipBg },
        '&.Mui-selected:hover': { backgroundColor: TS.tileBg },
        '&.Mui-focusVisible': { backgroundColor: TS.tileBg }
      }
    }
  },

  // ---- Buttons ----
  // contained = primary (ink). text and outlined = the white secondary button.
  MuiButton: {
    styleOverrides: {
      root: {
        height: 46,
        minHeight: 44,
        borderRadius: 10,
        padding: '0 18px',
        fontSize: 15,
        fontWeight: 700,
        letterSpacing: 0,
        textTransform: 'none',
        boxShadow: 'none',
        '&:hover': { boxShadow: 'none' },
        '&.Mui-focusVisible': focusRing
      },
      sizeSmall: { height: 44, padding: '0 14px', fontSize: 14 },
      contained: {
        backgroundColor: TS.primary,
        color: '#FFFFFF',
        '&:hover': { backgroundColor: TS.primaryHover },
        '&:active': { boxShadow: 'none' },
        // MUI turns pointer events off on disabled buttons, which also hides
        // the cursor; the native disabled attribute still blocks the click.
        '&.Mui-disabled': {
          backgroundColor: TS.border,
          color: TS.secondary,
          pointerEvents: 'auto',
          cursor: 'not-allowed'
        }
      },
      text: {
        backgroundColor: TS.card,
        border: `1px solid ${TS.btnBorder}`,
        color: TS.text,
        '&:hover': { backgroundColor: TS.page }
      },
      outlined: {
        backgroundColor: TS.card,
        border: `1px solid ${TS.btnBorder}`,
        color: TS.text,
        '&:hover': { backgroundColor: TS.page, borderColor: TS.btnBorder }
      },
      startIcon: { marginRight: 6 }
    }
  },
  MuiIconButton: {
    styleOverrides: {
      root: { '&.Mui-focusVisible': focusRing }
    }
  },
  // The app's primary is green, which a default checkbox would pick up. Ticked
  // is ink here; the 44px hit area comes from the padding.
  MuiCheckbox: {
    styleOverrides: {
      root: {
        padding: 10,
        color: TS.inputBorder,
        '&:hover': { backgroundColor: 'rgba(15, 23, 42, 0.05)' },
        '&.Mui-checked': { color: TS.text },
        '&.Mui-focusVisible': focusRing
      }
    }
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: { backgroundColor: TS.text, fontSize: 13, fontWeight: 500, padding: '6px 10px', borderRadius: 8 }
    }
  },

  // ---- Toast ----
  MuiAlert: {
    styleOverrides: {
      root: { borderRadius: 10, fontSize: 14, fontWeight: 500, alignItems: 'center' },
      message: { color: 'inherit' },
      standardSuccess: {
        backgroundColor: TS.text,
        color: '#FFFFFF',
        '& .MuiAlert-icon': { color: TS.amber }
      },
      standardError: {
        backgroundColor: TS.dangerBg,
        color: '#7F1D1D',
        border: `1px solid ${TS.dangerBorder}`,
        '& .MuiAlert-icon': { color: TS.danger }
      }
    }
  }
}

export const truckStopsTheme = outer => {
  const typography = typographyWithFont(outer, TS.font)
  typography.body1 = { ...typography.body1, fontSize: '0.9375rem', lineHeight: 1.45 }
  typography.body2 = { ...typography.body2, fontSize: '0.875rem', lineHeight: 1.45 }
  typography.button = { ...typography.button, textTransform: 'none', fontWeight: 700, letterSpacing: 0 }
  return createTheme({
    ...outer,
    palette: {
      ...outer.palette,
      text: { ...outer.palette.text, primary: TS.text, secondary: TS.secondary }
    },
    typography,
    components: { ...outer.components, ...components }
  })
}
